import { _ as _objectWithoutProperties, d as _slicedToArray, a as _objectSpread2, c as _defineProperty, w as withTestId, e as _arrayLikeToArray, f as _unsupportedIterableToArray, g as attachNextThemeRoot, h as applyThemeOverlay, u as useTheme$1, i as applyTestId, j as dataAttribute, m as motion$1, L as Loader } from './ProgressiveLoader-1f159f7e.esm.js';
import { forwardRef, useStyleConfig, chakra, useToast, useMergeRefs, useSafeLayoutEffect, useButtonGroup, Button as Button$1, Center, LightMode, DarkMode, Text, Link as Link$1 } from '@chakra-ui/react';
import { theme, mapResponsive } from '@hover/chakra-theme';
import { useEffect, useRef, useId, useMemo, useCallback } from 'react';
import { fromEntries, entries, capitalize, includes, invoke } from '@hover/utilities';
import { useReducedMotionConfig, MotionConfig, AnimatePresence } from 'framer-motion';
import 'ramda/src/omit';
import 'ramda/src/pick';
import { toCSSVar, CSSVars } from '@chakra-ui/system';
import 'ramda/src/has';
import 'tiny-warning';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import 'ramda/src/mapObjIndexed';
import 'ramda/src/join';
import 'ramda/src/identity';
import 'ramda/src/ifElse';
import 'ramda/src/map';
import 'ramda/src/split';
import 'ramda/src/concat';
import 'ramda/src/toLower';
import 'ramda/src/pipe';
import { motion, colorSchemes } from '@hover/chakra-theme/foundation';
import { theme as theme$1 } from '@hover/chakra-theme/next';
import { css, Global, useTheme, ThemeProvider } from '@emotion/react';
import { fontAdelleSans, fontMonoLisa } from '@hover/tokens-typography';
import { fontDiatype } from '@hover/tokens-typography/next';
import 'ramda/src/mergeDeepLeft';
import 'ramda/src/mergeLeft';
import '@hover/chakra-theme/bauhaus';
import 'tiny-invariant';
import { buttonSize, textAliases } from '@hover/chakra-theme/components';
import { STROKE_ACCENT_CLASS } from '@hover/icons/constants';
import { transitions } from 'polished';
import { createContext } from '@chakra-ui/react-utils';

/**
 * Whether to disable layout animations based on user preference or
 * environment.
 *
 * Both Chakra and Framer Motion expose hooks to read the user preference from
 * CSS, but this should be the canonical hook that we use as there are other
 * cases to consider (like screenshots in Chromatic).
 */
var useIsMotionReduced = function useIsMotionReduced(override) {
  // This reads from _both_ the CSS value, and the context value set via
  // `MotionConfig`, which allows us to set `reducedMotion="always"` in a
  // `MotionConfig` that wraps every story to disable motion in Chromatic.
  var isReduced = useReducedMotionConfig();
  return override !== null && override !== void 0 ? override : !!isReduced;
};

var _excluded$8 = ["accent", "icon", "orientation", "role", "strokeWidth", "sx", "isInline", "isStatic"];
var Icon = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _ref$accent = _ref.accent,
    accent = _ref$accent === void 0 ? 'currentColor' : _ref$accent,
    IconComponent = _ref.icon;
    _ref.orientation;
    var _ref$role = _ref.role,
    role = _ref$role === void 0 ? 'img' : _ref$role,
    strokeWidth = _ref.strokeWidth,
    sx = _ref.sx,
    isInline = _ref.isInline,
    isStatic = _ref.isStatic,
    props = _objectWithoutProperties(_ref, _excluded$8);
  var styles = useStyleConfig('Icon', props);

  // WTF: for some reason, this specific component was throwing hydration
  // mismatch errors when being server rendered (only in **web-react**, I
  // wasn't able to reproduce this in the documentation or demo application).
  // It turns out the issue was, for some reason, the style object properties
  // were in different orders which was producing a different checksum on the
  // client vs. the server. I think the true offender is somewhere in Chakra
  // or Emotion, but this'll do for now, pig.
  var sortedStyles = Object.fromEntries(Object.entries(styles).sort(function (_ref2, _ref3) {
    var _ref4 = _slicedToArray(_ref2, 1),
      a = _ref4[0];
    var _ref5 = _slicedToArray(_ref3, 1),
      b = _ref5[0];
    return a > b ? 1 : -1;
  }));
  var customStrokeWidth = strokeWidth ? {
    strokeWidth: strokeWidth
  } : {};
  var transition = isStatic ? {} : transitions(['color'], theme.motion.easings[250]);
  return jsx(chakra.svg, _objectSpread2(_objectSpread2(_objectSpread2({
    "aria-hidden": props.title ? 'false' : 'true',
    as: IconComponent,
    display: isInline ? 'inline' : undefined,
    margin: isInline ? 0 : undefined,
    role: role
  }, transition), withTestId(props)), {}, {
    __css: sortedStyles,
    ref: ref,
    sx: _objectSpread2(_objectSpread2(_objectSpread2({}, customStrokeWidth), sx), {}, _defineProperty({}, ".".concat(STROKE_ACCENT_CLASS), {
      stroke: accent
    }))
  }));
});
Icon.displayName = 'Icon';

var Motion = /*#__PURE__*/fromEntries( /*#__PURE__*/entries(motion.framer).map(function (_ref) {
  var _ref2 = _slicedToArray(_ref, 2),
    name = _ref2[0],
    configuration = _ref2[1];
  return [capitalize(name), function (props) {
    return jsx(MotionConfig, _objectSpread2({
      transition: configuration
    }, props));
  }];
}));

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

var _excluded$7 = ["host"];
var ASSET_HOST = 'https://blueprint.hover.to/assets/typography/';
var fontStylesFactory = function fontStylesFactory(fn) {
  return function (_ref) {
    var host = _ref.host,
      rest = _objectWithoutProperties(_ref, _excluded$7);
    return (
      // @ts-ignore
      fn(_objectSpread2({
        host: host !== null && host !== void 0 ? host : ASSET_HOST
      }, rest))
    );
  };
};

/**
 * Outputs @font-face declarations for Adelle Sans font. Unlike the other font
 * styles factories, Adelle Sans has two variations: original and "shifted" on
 * account of the raised baseline Adelle has out of the box. The "shifted"
 * version will load alternative assets what have the baseline shifted down 100
 * units (in 1000 units-per-em space)
 *
 * @param host prefix for asset URL
 * @param isShifted whether to load "shifted" assets or not (defaults to true)
 */
var adelleSans = /*#__PURE__*/fontStylesFactory(function (_ref) {
  var host = _ref.host,
    _ref$isShifted = _ref.isShifted,
    isShifted = _ref$isShifted === void 0 ? true : _ref$isShifted;
  var suffix = isShifted ? '-hover' : '';
  return /*#__PURE__*/css("@font-face{font-family:", fontAdelleSans, ";src:url(", host, "adelle-sans", suffix, "--light.otf) format('opentype');font-weight:300;font-style:normal;font-display:fallback;}@font-face{font-family:", fontAdelleSans, ";src:url(", host, "adelle-sans", suffix, "--light-italic.otf) format('opentype');font-weight:300;font-style:italic;font-display:fallback;}@font-face{font-family:", fontAdelleSans, ";src:url(", host, "adelle-sans", suffix, "--regular.otf) format('opentype');font-weight:400;font-style:normal;font-display:fallback;}@font-face{font-family:", fontAdelleSans, ";src:url(", host, "adelle-sans", suffix, "--italic.otf) format('opentype');font-weight:400;font-style:italic;font-display:fallback;}@font-face{font-family:", fontAdelleSans, ";src:url(", host, "adelle-sans", suffix, "--semi-bold.otf) format('opentype');font-weight:600;font-style:normal;font-display:fallback;}@font-face{font-family:", fontAdelleSans, ";src:url(", host, "adelle-sans", suffix, "--semi-bold-italic.otf) format('opentype');font-weight:600;font-style:italic;font-display:fallback;}@font-face{font-family:", fontAdelleSans, ";src:url(", host, "adelle-sans", suffix, "--bold.otf) format('opentype');font-weight:700;font-style:normal;font-display:fallback;}@font-face{font-family:", fontAdelleSans, ";src:url(", host, "adelle-sans", suffix, "--bold-italic.otf) format('opentype');font-weight:700;font-style:italic;font-display:fallback;}" + (process.env.NODE_ENV === "production" ? "" : ";label:adelleSans;"), process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */");
});

var diatype = /*#__PURE__*/fontStylesFactory(function (_ref) {
  var host = _ref.host;
  return /*#__PURE__*/css("@font-face{font-family:", fontDiatype, ";src:url(", host, "diatype-bold-italic.woff2) format('woff2'),url(", host, "diatype-bold-italic.woff) format('woff'),url(", host, "diatype-bold-italic.otf) format('opentype');font-weight:bold;font-style:italic;font-display:fallback;}@font-face{font-family:", fontDiatype, ";src:url(", host, "diatype-bold.woff2) format('woff2'),url(", host, "diatype-bold.woff) format('woff'),url(", host, "diatype-bold.otf) format('opentype');font-weight:bold;font-style:normal;font-display:fallback;}@font-face{font-family:", fontDiatype, ";src:url(", host, "diatype-medium-italic.woff2) format('woff2'),url(", host, "diatype-medium-italic.woff) format('woff'),url(", host, "diatype-medium-italic.otf) format('opentype');font-weight:500;font-style:italic;font-display:fallback;}@font-face{font-family:", fontDiatype, ";src:url(", host, "diatype-medium.woff2) format('woff2'),url(", host, "diatype-medium.woff) format('woff'),url(", host, "diatype-medium.otf) format('opentype');font-weight:500;font-style:normal;font-display:fallback;}@font-face{font-family:", fontDiatype, ";src:url(", host, "diatype-regular-italic.woff2) format('woff2'),url(", host, "diatype-regular-italic.woff) format('woff'),url(", host, "diatype-regular-italic.otf) format('opentype');font-weight:normal;font-style:italic;font-display:fallback;}@font-face{font-family:", fontDiatype, ";src:url(", host, "diatype-regular.woff2) format('woff2'),url(", host, "diatype-regular.woff) format('woff'),url(", host, "diatype-regular.otf) format('opentype');font-weight:normal;font-style:normal;font-display:fallback;}" + (process.env.NODE_ENV === "production" ? "" : ";label:diatype;"), process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */");
});

var monoLisa = /*#__PURE__*/fontStylesFactory(function (_ref) {
  var host = _ref.host;
  return /*#__PURE__*/css("@font-face{font-family:", fontMonoLisa, ";src:url(", host, "mono-lisa--light.ttf) format('truetype');font-weight:300;font-style:normal;font-display:fallback;}@font-face{font-family:", fontMonoLisa, ";src:url(", host, "mono-lisa--light-italic.ttf) format('truetype');font-weight:300;font-style:italic;font-display:fallback;}@font-face{font-family:", fontMonoLisa, ";src:url(", host, "mono-lisa--regular.ttf) format('truetype');font-weight:400;font-style:normal;font-display:fallback;}@font-face{font-family:", fontMonoLisa, ";src:url(", host, "mono-lisa--regular-italic.ttf) format('truetype');font-weight:400;font-style:italic;font-display:fallback;}@font-face{font-family:", fontMonoLisa, ";src:url(", host, "mono-lisa--medium.ttf) format('truetype');font-weight:500;font-style:normal;font-display:fallback;}@font-face{font-family:", fontMonoLisa, ";src:url(", host, "mono-lisa--medium-italic.ttf) format('truetype');font-weight:500;font-style:italic;font-display:fallback;}@font-face{font-family:", fontMonoLisa, ";src:url(", host, "mono-lisa--bold.ttf) format('truetype');font-weight:700;font-style:normal;font-display:fallback;}@font-face{font-family:", fontMonoLisa, ";src:url(", host, "mono-lisa--bold-italic.ttf) format('truetype');font-weight:700;font-style:italic;font-display:fallback;}" + (process.env.NODE_ENV === "production" ? "" : ";label:monoLisa;"), process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */");
});

var _excluded$6 = ["includeNew", "includeLegacy"];
var Fonts = function Fonts(_ref) {
  var _ref$includeNew = _ref.includeNew,
    includeNew = _ref$includeNew === void 0 ? false : _ref$includeNew,
    _ref$includeLegacy = _ref.includeLegacy,
    includeLegacy = _ref$includeLegacy === void 0 ? true : _ref$includeLegacy,
    props = _objectWithoutProperties(_ref, _excluded$6);
  var commonFonts = [monoLisa(props)];
  var newFonts = [diatype(props)];
  var legacyFonts = [adelleSans(props)];
  return jsx(Global, {
    styles: /*#__PURE__*/css([].concat(commonFonts, _toConsumableArray(includeNew ? newFonts : []), _toConsumableArray(includeLegacy ? legacyFonts : [])), process.env.NODE_ENV === "production" ? "" : ";label:Fonts;", process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbIkZvbnRzLnRzeCJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUF3QmMiLCJmaWxlIjoiRm9udHMudHN4Iiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgR2xvYmFsLCBjc3MgfSBmcm9tICdAZW1vdGlvbi9yZWFjdCc7XG5pbXBvcnQgUmVhY3QgZnJvbSAncmVhY3QnO1xuXG5pbXBvcnQgeyBhZGVsbGVTYW5zIH0gZnJvbSAnLi9hZGVsbGVTYW5zJztcbmltcG9ydCB7IGRpYXR5cGUgfSBmcm9tICcuL2RpYXR5cGUnO1xuaW1wb3J0IHsgbW9ub0xpc2EgfSBmcm9tICcuL21vbm9MaXNhJztcbmltcG9ydCB7IEZvbnRQcm9wcyB9IGZyb20gJy4vdHlwZXMnO1xuXG5leHBvcnQgaW50ZXJmYWNlIEZvbnRzUHJvcHMgZXh0ZW5kcyBGb250UHJvcHMge1xuICBpbmNsdWRlTmV3PzogYm9vbGVhbjtcbiAgaW5jbHVkZUxlZ2FjeT86IGJvb2xlYW47XG59XG5cbmNvbnN0IEZvbnRzID0gKHtcbiAgaW5jbHVkZU5ldyA9IGZhbHNlLFxuICBpbmNsdWRlTGVnYWN5ID0gdHJ1ZSxcbiAgLi4ucHJvcHNcbn06IEZvbnRzUHJvcHMpID0+IHtcbiAgY29uc3QgY29tbW9uRm9udHMgPSBbbW9ub0xpc2EocHJvcHMpXTtcbiAgY29uc3QgbmV3Rm9udHMgPSBbZGlhdHlwZShwcm9wcyldO1xuICBjb25zdCBsZWdhY3lGb250cyA9IFthZGVsbGVTYW5zKHByb3BzKV07XG5cbiAgcmV0dXJuIChcbiAgICA8R2xvYmFsXG4gICAgICBzdHlsZXM9e2NzcyhbXG4gICAgICAgIC4uLmNvbW1vbkZvbnRzLFxuICAgICAgICAuLi4oaW5jbHVkZU5ldyA/IG5ld0ZvbnRzIDogW10pLFxuICAgICAgICAuLi4oaW5jbHVkZUxlZ2FjeSA/IGxlZ2FjeUZvbnRzIDogW10pLFxuICAgICAgXSl9XG4gICAgLz5cbiAgKTtcbn07XG5cbmV4cG9ydCB7IEZvbnRzIH07XG4iXX0= */")
  });
};

var _excluded$5 = ["sx"];
var _createContext$2 = /*#__PURE__*/createContext({
    name: 'PaintToastContext'
  }),
  _createContext2$2 = /*#__PURE__*/_slicedToArray(_createContext$2, 2),
  Provider$1 = _createContext2$2[0],
  useContext$1 = _createContext2$2[1];
var useToastTargetRefs = function useToastTargetRefs() {
  var toastPortalRef = useRef(null);
  var toastTargetRef = function toastTargetRef(element) {
    if (typeof window === 'undefined') return;
    toastPortalRef.current = element !== null && element !== void 0 ? element : document.body;
  };
  useSafeLayoutEffect(function () {
    if (toastPortalRef.current === null) {
      toastPortalRef.current = document.body;
    }
  }, []);
  return {
    toastPortalRef: toastPortalRef,
    toastTargetRef: toastTargetRef
  };
};
var ToastTarget = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var sx = _ref.sx,
    props = _objectWithoutProperties(_ref, _excluded$5);
  var _useContext = useContext$1(),
    targetRef = _useContext.containerRef;
  var toast = useToast();
  useEffect(function () {
    var reset = function reset() {
      toast.closeAll();
      toast({
        duration: 0,
        render: function render() {
          return null;
        }
      });
    };
    reset();
    return reset;
  }, [toast]);
  return jsx(chakra.div, _objectSpread2(_objectSpread2({
    position: "relative",
    sx: _objectSpread2(_objectSpread2({}, sx), {}, {
      '& ul': {
        padding: '0 !important',
        position: 'absolute !important'
      },
      '& #chakra-toast-manager-top': {
        top: '0 !important',
        left: '0 !important',
        right: '0 !important'
      },
      '& #chakra-toast-manager-bottom': {
        bottom: '0 !important',
        right: '0 !important',
        left: '0 !important'
      },
      '& #chakra-toast-manager-top-left': {
        top: '0 !important',
        left: '0 !important'
      },
      '& #chakra-toast-manager-top-right': {
        top: '0 !important',
        right: '0 !important'
      },
      '& #chakra-toast-manager-bottom-left': {
        bottom: '0 !important',
        left: '0 !important'
      },
      '& #chakra-toast-manager-bottom-right': {
        bottom: '0 !important',
        right: '0 !important'
      }
    })
  }, props), {}, {
    ref: useMergeRefs(ref, targetRef)
  }));
});

/**
 * Generate a unique name for a CSS selector that's SSR-safe using React's
 * `useId` hook. The problem with `useId` is that it includes colons in the
 * generated ID, which isn't supported for CSS selectors.
 *
 * @param prefix
 * @param separator
 *
 * @returns unique string that can be used as a CSS selector
 */
var useUniqueSelector = function useUniqueSelector(prefix) {
  var separator = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '--';
  return "".concat(prefix).concat(useId().replace(':', separator).replace(':', ''));
};

var _excluded$4 = ["attach", "children", "next"];
var _createContext$1 = /*#__PURE__*/createContext({
    name: 'NextFeaturesContext',
    strict: false
  }),
  _createContext2$1 = /*#__PURE__*/_slicedToArray(_createContext$1, 2),
  NextFeaturesProvider = _createContext2$1[0],
  useNextFeaturesContext = _createContext2$1[1];
var useIsNextFeatureEnabled = function useIsNextFeatureEnabled(feature) {
  var next = useNextFeaturesContext();
  if (!next) return false;
  if (next === 'all') return true;
  return next.includes(feature);
};

/**
 * Apply a modified theme to a component subtree nested in an existing theme
 * with the specified `features` enabled. This attaches CSS variables that
 * override the outer theme's CSS variables, and includes fonts when they are
 * marked as enabled in `features`.
 */
var NestedThemeProvider = function NestedThemeProvider(_ref) {
  var selector = _ref.selector,
    children = _ref.children,
    _ref$features = _ref.features,
    features = _ref$features === void 0 ? 'all' : _ref$features,
    _ref$includeFonts = _ref.includeFonts,
    includeFonts = _ref$includeFonts === void 0 ? true : _ref$includeFonts,
    fontsHost = _ref.fontsHost,
    theme = _ref.theme;
  var computedTheme = useMemo(function () {
    return toCSSVar(theme);
  }, [theme]);
  return jsxs(ThemeProvider, {
    theme: computedTheme,
    children: [jsx(CSSVars, {
      root: selector
    }), jsx(Fonts, {
      host: fontsHost,
      includeLegacy: false,
      includeNew: includeFonts && (features === 'all' || features.includes('fonts'))
    }), jsx(NextFeaturesProvider, {
      value: features,
      children: children
    })]
  });
};
var _createContext3 = /*#__PURE__*/createContext({
    name: 'NextThemeRootContext',
    strict: false
  }),
  _createContext4 = /*#__PURE__*/_slicedToArray(_createContext3, 2),
  NextThemeRootProvider = _createContext4[0],
  useNextThemeRoot = _createContext4[1];

/**
 * Get the CSS class name that next theme CSS variables will be attached to
 */
var useNextThemeRootClassName = function useNextThemeRootClassName() {
  var _context$className;
  var context = useNextThemeRoot();
  return (_context$className = context === null || context === void 0 ? void 0 : context.className) !== null && _context$className !== void 0 ? _context$className : '';
};
var NextProvider = function NextProvider(_ref2) {
  var _ref2$attach = _ref2.attach,
    attach = _ref2$attach === void 0 ? false : _ref2$attach,
    childrenProp = _ref2.children,
    _ref2$next = _ref2.next,
    next = _ref2$next === void 0 ? 'all' : _ref2$next,
    props = _objectWithoutProperties(_ref2, _excluded$4);
  var className = useUniqueSelector('next-provider-root');
  var selector = ".".concat(className);
  var children = attach ? attachNextThemeRoot(childrenProp, className) : childrenProp;
  var outerTheme = useTheme();
  var innerTheme = applyThemeOverlay(outerTheme, theme$1, next);
  return jsx(NextThemeRootProvider, {
    value: {
      className: className
    },
    children: jsx(NestedThemeProvider, _objectSpread2(_objectSpread2({}, props), {}, {
      features: next,
      selector: selector,
      theme: innerTheme,
      children: children
    }))
  });
};

var _createContext = /*#__PURE__*/createContext({
    name: 'ColorSchemeContext',
    strict: false
  }),
  _createContext2 = /*#__PURE__*/_slicedToArray(_createContext, 3),
  Provider = _createContext2[0],
  useContext = _createContext2[1];
  _createContext2[2];
var useColorScheme = function useColorScheme() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    colorScheme = _ref.colorScheme;
  var context = useContext();
  return _objectSpread2({
    colorScheme: colorScheme
  }, context);
};
var ColorSchemeProvider = function ColorSchemeProvider(_ref2) {
  var colorScheme = _ref2.colorScheme,
    variant = _ref2.variant,
    children = _ref2.children;
  return jsx(Provider, {
    value: {
      colorScheme: typeof colorScheme === 'string' && includes(colorSchemes, colorScheme) ? colorScheme : undefined,
      variant: variant
    },
    children: children
  });
};

var getThemeHeight = function getThemeHeight(styles) {
  var _invoke = invoke(styles),
    height = _invoke.height;
  return typeof height === 'string' || typeof height === 'number' ? height : 'auto';
};

/**
 * Conditionally return props to be spread into button to make it square based
 * on the `height` defined for the specified `size` on the theme
 *
 * @param equal whether `width` and `height` should be equal
 * @param props button props
 *
 * @returns props to be spread into button
 */
var useSquareProps = function useSquareProps(equal, size) {
  // NOTE: this is an affordance for unit tests that don't properly wrap the
  // component under test in the system provider. They really all should be
  // wrapped properly, but it doesn't seem worth breaking a bunch of existing
  // tests to enforce it when they don't assert anything visual anyways.
  var theme = useTheme$1();
  var sizes = theme && 'components' in theme ? theme.components.Button.sizes : undefined;
  if (!equal || !sizes) return {
    size: size
  };
  var dimension = mapResponsive(function (s) {
    return includes(buttonSize, s) ? getThemeHeight(sizes[s]) : getThemeHeight(sizes["default"]);
  }, size);
  return {
    width: dimension,
    height: dimension,
    paddingX: '0 !important'
  };
};

var _excluded$3 = ["children", "color", "colorScheme", "fill", "iconAfter", "isDisabled", "isLoading", "iconBefore", "label", "onClick", "shape", "size", "variant"];
var MotionCenter = /*#__PURE__*/motion$1(Center);
var MotionLoader = /*#__PURE__*/motion$1(Loader);
var iconSizes = {
  small: 'tiny',
  "default": 'small',
  medium: 'small',
  large: 'small'
};
var loaderSizes = {
  small: 'tiny',
  "default": 'small',
  medium: 'small',
  large: 'medium'
};
var fillToVariant = {
  solid: 'primary',
  outline: 'tertiary',
  minimal: 'ghost'
};
var transformForFill = function transformForFill(translate, transform) {
  return translate ? {
    transform: transform
  } : {};
};
var getVariants = function getVariants(translate, loader) {
  return {
    loading: _objectSpread2({
      opacity: loader ? 1 : 0
    }, transformForFill(translate, "translate3d(0, ".concat(loader ? 0 : -50, "%, 0)"))),
    idle: _objectSpread2({
      opacity: loader ? 0 : 1
    }, transformForFill(translate, "translate3d(0, ".concat(loader ? 100 : 0, "%, 0)")))
  };
};
var ForceMode = function ForceMode(_ref) {
  var colorScheme = _ref.colorScheme,
    children = _ref.children;
  var isNext = useIsNextFeatureEnabled('components');
  if (!isNext) return children;
  if (colorScheme === 'dark') return jsx(LightMode, {
    children: children
  });
  if (colorScheme === 'light') return jsx(DarkMode, {
    children: children
  });
  return children;
};
var Button = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var _ref3, _ref4;
  var children = _ref2.children,
    _ref2$color = _ref2.color,
    color = _ref2$color === void 0 ? 'primary' : _ref2$color,
    colorSchemeProp = _ref2.colorScheme,
    _ref2$fill = _ref2.fill,
    fill = _ref2$fill === void 0 ? 'solid' : _ref2$fill,
    iconAfter = _ref2.iconAfter,
    isDisabledProp = _ref2.isDisabled,
    isLoading = _ref2.isLoading,
    iconBefore = _ref2.iconBefore,
    label = _ref2.label,
    onClick = _ref2.onClick,
    _ref2$shape = _ref2.shape,
    shape = _ref2$shape === void 0 ? 'pill' : _ref2$shape,
    sizeProp = _ref2.size,
    variantProp = _ref2.variant,
    props = _objectWithoutProperties(_ref2, _excluded$3);
  var group = useButtonGroup();
  var colorScheme = colorSchemeProp !== null && colorSchemeProp !== void 0 ? colorSchemeProp : group === null || group === void 0 ? void 0 : group.colorScheme;
  var variant = variantProp !== null && variantProp !== void 0 ? variantProp : group === null || group === void 0 ? void 0 : group.variant;
  var size = (_ref3 = sizeProp !== null && sizeProp !== void 0 ? sizeProp : group === null || group === void 0 ? void 0 : group.size) !== null && _ref3 !== void 0 ? _ref3 : 'default';
  var isDisabled = isDisabledProp !== null && isDisabledProp !== void 0 ? isDisabledProp : group === null || group === void 0 ? void 0 : group.isDisabled;
  var isNext = useIsNextFeatureEnabled('components');
  var isMotionReduced = useIsMotionReduced();
  var shouldSlide = fill !== 'minimal' && !isMotionReduced;

  // Apply equal width and height read from the theme for `square` and
  // `circle` button shapes. This is mostly used for `IconButton`, but it is
  // also possible to specify this shape independent of `IconButton`.
  var isSquare = shape === 'circle' || shape === 'square';
  var squareProps = useSquareProps(isSquare, size);

  // TODO: maybe we shouldn't be setting `aria-label` to the text content of
  // the button here? I'm not sure if it's actually harmful, but I'm leaving
  // it for now to avoid a potential breaking change
  var ariaLabel = (_ref4 = label !== null && label !== void 0 ? label : props['aria-label']) !== null && _ref4 !== void 0 ? _ref4 : typeof children === 'string' ? children : undefined;
  var iconSize = mapResponsive(function (s) {
    return iconSizes[s];
  }, size, 'small');
  var loaderSize = mapResponsive(function (s) {
    return loaderSizes[s];
  }, size, 'small');
  return jsx(ForceMode, {
    colorScheme: colorScheme !== null && colorScheme !== void 0 ? colorScheme : color,
    children: jsx(Button$1, _objectSpread2(_objectSpread2(_objectSpread2({
      "aria-label": ariaLabel,
      borderRadius: !isNext && includes(['square', 'box'], shape) ? '300' : '900',
      colorScheme: isNext ? colorScheme : color,
      "data-loading": dataAttribute(isLoading),
      disabled: isDisabled || isLoading,
      isDisabled: isDisabled,
      onClick: onClick,
      variant: isNext ? variant !== null && variant !== void 0 ? variant : fillToVariant[fill] : fill
    }, squareProps), withTestId(props)), {}, {
      ref: ref,
      children: jsxs(Motion.Tight, {
        children: [jsxs(MotionCenter, _objectSpread2(_objectSpread2({}, applyTestId({
          testId: 'wrapper'
        })), {}, {
          animate: isLoading ? 'loading' : 'idle',
          height: "100%",
          variants: getVariants(shouldSlide, false),
          width: "100%",
          children: [iconBefore && jsx(Icon, {
            icon: iconBefore,
            margin: "0 !important",
            marginRight: "200 !important",
            size: iconSize
          }), isSquare ? children : jsx(chakra.span, {
            className: "button-label",
            display: "inline",
            children: children
          }), iconAfter && jsx(Icon, {
            icon: iconAfter,
            margin: "0 !important",
            marginLeft: "200 !important",
            size: iconSize
          })]
        })), jsx(AnimatePresence, {
          children: isLoading && jsx(MotionLoader, {
            animate: "loading",
            color: "inherit",
            exit: "idle",
            initial: "idle",
            position: "absolute",
            size: loaderSize,
            variants: getVariants(shouldSlide, true)
          })
        })]
      })
    }))
  });
});
Button.displayName = 'Button';

var _excluded$2 = ["icon", "iconSize", "shape", "size"];
var IconButton = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var icon = _ref.icon,
    iconSize = _ref.iconSize,
    _ref$shape = _ref.shape,
    shape = _ref$shape === void 0 ? 'circle' : _ref$shape,
    size = _ref.size,
    props = _objectWithoutProperties(_ref, _excluded$2);
  return jsx(Button, _objectSpread2(_objectSpread2({
    shape: shape,
    size: size
  }, props), {}, {
    ref: ref,
    children: jsx(Icon, {
      icon: icon,
      size: iconSize !== null && iconSize !== void 0 ? iconSize : mapResponsive(function (s) {
        return s ? iconSizes[s] : 'small';
      }, size)
    })
  }));
});
IconButton.displayName = 'IconButton';

var _excluded$1 = ["as", "children", "element", "isSemiBold", "isBold", "size", "withCapsize"];
var aliasNames = /*#__PURE__*/Object.keys(textAliases);
var getAlias = function getAlias(alias) {
  return typeof alias === 'string' && includes(aliasNames, alias) ? textAliases[alias] : alias;
};
var getSizeProps = function getSizeProps(sizeProp, withCapsize) {
  var size = mapResponsive(function (s) {
    return "".concat(getAlias(s));
  }, sizeProp);
  var textStyle = withCapsize ? mapResponsive(function (s) {
    return "body".concat(getAlias(s), "WithCapsize");
  }, sizeProp) : undefined;
  return {
    size: size,
    textStyle: textStyle
  };
};
var Body = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var as = _ref.as,
    children = _ref.children,
    element = _ref.element,
    _ref$isSemiBold = _ref.isSemiBold,
    isSemiBold = _ref$isSemiBold === void 0 ? false : _ref$isSemiBold,
    _ref$isBold = _ref.isBold,
    isBold = _ref$isBold === void 0 ? false : _ref$isBold,
    _ref$size = _ref.size,
    sizeProp = _ref$size === void 0 ? 500 : _ref$size,
    _ref$withCapsize = _ref.withCapsize,
    withCapsize = _ref$withCapsize === void 0 ? false : _ref$withCapsize,
    props = _objectWithoutProperties(_ref, _excluded$1);
  var _getSizeProps = getSizeProps(sizeProp, withCapsize),
    size = _getSizeProps.size,
    textStyle = _getSizeProps.textStyle;
  var getVariant = function getVariant() {
    if (isBold) return 'bold';
    if (isSemiBold) return 'semiBold';
    return 'default';
  };
  return jsx(Text, _objectSpread2(_objectSpread2({
    as: element !== null && element !== void 0 ? element : as,
    textStyle: textStyle
  }, withTestId(props)), {}, {
    ref: ref,
    size: size,
    variant: getVariant(),
    children: children
  }));
});
Body.displayName = 'Body';

var _excluded = ["children", "iconAfter", "iconBefore", "isMinimal", "isUnderlined", "label", "onChange", "onPress", "size", "withCapsize"];
var iconProps = {
  color: 'inherit',
  height: '1em',
  margin: 0,
  width: '1em'
};
var Link = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _props$colorScheme;
  var children = _ref.children,
    iconAfter = _ref.iconAfter,
    iconBefore = _ref.iconBefore,
    _ref$isMinimal = _ref.isMinimal,
    isMinimal = _ref$isMinimal === void 0 ? false : _ref$isMinimal,
    _ref$isUnderlined = _ref.isUnderlined,
    isUnderlined = _ref$isUnderlined === void 0 ? false : _ref$isUnderlined,
    label = _ref.label,
    onChange = _ref.onChange,
    onPress = _ref.onPress,
    sizeProp = _ref.size,
    _ref$withCapsize = _ref.withCapsize,
    withCapsize = _ref$withCapsize === void 0 ? false : _ref$withCapsize,
    props = _objectWithoutProperties(_ref, _excluded);
  var onClick = useCallback(function (event) {
    onChange === null || onChange === void 0 ? void 0 : onChange(event);
    if (!props.disabled) onPress === null || onPress === void 0 ? void 0 : onPress(event);
  }, [onChange, props.disabled, onPress]);

  // We only want to default `size` when `Link` is rendered as a button,
  // otherwise we want it to inherit `font-size` and `line-height` by default
  var defaultedSize = props.as === 'button' && !sizeProp ? 500 : sizeProp;
  var _ref2 = defaultedSize ? getSizeProps(defaultedSize, withCapsize) : {
      size: undefined,
      textStyle: undefined
    },
    size = _ref2.size,
    textStyle = _ref2.textStyle;
  var getVariant = function getVariant() {
    if (isMinimal) return 'minimal';
    return isUnderlined ? 'persistent-underline' : 'underline';
  };
  return jsx(Link$1, _objectSpread2(_objectSpread2({
    "aria-label": label,
    onClick: onClick
  }, withTestId(props)), {}, {
    ref: ref,
    size: size,
    variant: getVariant(),
    children: jsxs(ColorSchemeProvider, {
      colorScheme: (_props$colorScheme = props.colorScheme) !== null && _props$colorScheme !== void 0 ? _props$colorScheme : 'primary',
      children: [iconBefore && jsx(Icon, _objectSpread2(_objectSpread2({}, iconProps), {}, {
        icon: iconBefore,
        marginRight: "0.25em"
      })), withCapsize ? jsx(chakra.span, {
        textStyle: textStyle,
        children: children
      }) : children, iconAfter && jsx(Icon, _objectSpread2(_objectSpread2({}, iconProps), {}, {
        icon: iconAfter,
        marginLeft: "0.25em"
      }))]
    })
  }));
});
Link.displayName = 'Link';

export { Body as B, ColorSchemeProvider as C, Fonts as F, Icon as I, Link as L, Motion as M, NextThemeRootProvider as N, Provider$1 as P, ToastTarget as T, _toConsumableArray as _, useNextFeaturesContext as a, NestedThemeProvider as b, useIsNextFeatureEnabled as c, Button as d, IconButton as e, useColorScheme as f, getVariants as g, useIsMotionReduced as h, NextProvider as i, useNextThemeRootClassName as j, useToastTargetRefs as k, NextFeaturesProvider as l, useUniqueSelector as u };
